import '@splidejs/splide/dist/css/splide.min.css';
import '@splidejs/splide/dist/css/splide-core.min.css';
import Splide from '@splidejs/splide';
import { bindCarousel } from '@/ts/utilities';
import { AV_LANGUAGES } from '@/ts/enums';

/**
 * This module is responsible for carousel initialization across website.
 */
module Carousel {
    const $carousels = document.querySelectorAll('.carousel');

    if ($carousels.length) {
        $carousels.forEach(($item: Element) => {
            const $carouselWrapper = $item.querySelector('.carousel-wrapper') as HTMLElement;

            if (!$carouselWrapper) {
                return;
            }

            const $slides = $carouselWrapper.querySelectorAll('.splide__slide');
            const $elementContainer = $carouselWrapper.querySelector('.views-element-container') as HTMLElement;
            const htmlElement = document.querySelector('html');
            const htmlLang: string = htmlElement ? htmlElement.getAttribute('lang') || '' : '';
            const currentLanguage: string = (htmlLang && AV_LANGUAGES.find(lang => lang.includes(htmlLang))) || 'en-GB';

            function create() {
                const splide = document.createElement('div');
                splide.classList.add('splide');
                const splideTrack = document.createElement('div');
                splideTrack.classList.add('splide__track');
                const splideList = document.createElement('div');
                splideList.classList.add('splide__list');

                if ($elementContainer) {
                    $elementContainer.appendChild(splide);
                    splide.appendChild(splideTrack);
                    splideTrack.appendChild(splideList);
                } else {
                    $carouselWrapper.appendChild(splide);
                    splide.appendChild(splideTrack);
                    splideTrack.appendChild(splideList);
                }

                if ($slides.length) {
                    $slides.forEach(($slide: Element) => {
                        const $cloneSlide = $slide.cloneNode(true);
                        splideList.appendChild($cloneSlide);
                        $slide.remove();
                    });
                }
            }

            function initCarousel() {
                create();

                const $carousel = $carouselWrapper.querySelector('.splide') as HTMLElement;
                if ($carousel && !$carousel.classList.contains('is-initialized')) {
                    const $cards = $item.querySelectorAll('.carousel-wrapper .card');
                    const $news = $item.querySelectorAll('.carousel-wrapper .news-teaser.splide__slide');

                    //@ts-ignore
                    const $splide = new Splide($carousel, {
                        type: 'slide',
                        autoplay: false,
                        omitEnd: true,
                        focus: 0,
                        autoWidth: true,
                        gap: 24,
                        arrows: !$item.classList.contains('blue-bg') ? $cards.length > 4 || $news.length > 4 : $cards.length > 2 || $news.length > 4,
                        breakpoints: {
                            1500: {
                                arrows: !$item.classList.contains('blue-bg') ? $cards.length > 3 || $news.length > 3 : $cards.length > 1 || $news.length > 3,
                            },
                            1030: {
                                arrows: !$item.classList.contains('blue-bg') ? $cards.length > 2 || $news.length > 2 : $cards.length > 1 || $news.length > 2,
                            },
                            991: {
                                perPage: 1,
                                perMove: 1,
                                arrows: $cards.length > 1 || $news.length > 1,
                            },
                        },
                        i18n: {
                            prev: currentLanguage === 'it-IT' ? 'Slide precedente' : 'Previous slide',
                            next: currentLanguage === 'it-IT' ? 'Slide successiva' : 'Next slide',
                            slideX: currentLanguage === 'it-IT' ? 'Vai alla slide %s' : 'Go to slide %s',
                            slideLabel: currentLanguage === 'it-IT' ? '%s di %s' : '%s of %s',
                        },
                    });

                    $splide.on('pagination:mounted', function () {
                        if (window.innerWidth < 992) {
                            setVisibleBullets($item, 0, 0, 0, true);
                        }
                    });

                    $splide.mount({}, bindCarousel);

                    $splide.on('move', (succ, prev, active) => {
                        setVisibleBullets($carousel, prev, succ, active);
                    });
                }
            }

            function initCarouselMobile() {
                create();

                const $carousel = $carouselWrapper.querySelector('.splide') as HTMLElement;
                if ($carousel && !$carousel.classList.contains('is-initialized')) {
                    const itemsPerPage = parseInt($item.getAttribute('data-items-per-page') || 'NaN') || 1;
                    const isMoreThanOnePage = Boolean($slides.length > itemsPerPage);

                    //@ts-ignore
                    const $splide = new Splide($carousel, {
                        type: 'slide',
                        autoplay: false,
                        perPage: itemsPerPage,
                        perMove: itemsPerPage,
                        gap: 24,
                        omitEnd: true,
                        arrows: isMoreThanOnePage,
                        pagination: isMoreThanOnePage,
                        breakpoints: {
                            991: {
                                perPage: 1,
                                arrows: $slides.length > 1,
                                pagination: $slides.length > 1,
                            },
                        },
                        i18n: {
                            prev: currentLanguage === 'it-IT' ? 'Slide precedente' : 'Previous slide',
                            next: currentLanguage === 'it-IT' ? 'Slide successiva' : 'Next slide',
                            slideX: currentLanguage === 'it-IT' ? 'Vai alla slide %s' : 'Go to slide %s',
                            slideLabel: currentLanguage === 'it-IT' ? '%s di %s' : '%s of %s',
                            select: currentLanguage === 'it-IT' ? 'Seleziona la slide da visualizzare' : 'Select a slide to show'
                        },
                    });

                    $splide.on('pagination:mounted', function () {
                        if (window.innerWidth < 992) {
                            setVisibleBullets($item, 0, 0, 0, true);
                        }
                    });

                    $splide.mount({}, bindCarousel);

                    $splide.on('move', (succ, prev, active) => {
                        setVisibleBullets($carousel, prev, succ, active);
                    });
                }
            }

            function bindUI() {
                if ($item.classList.contains('carousel-version')) {
                    initCarousel();
                } else {
                    initCarouselMobile();
                }
                $item.classList.remove('d-none');
                $carouselWrapper.classList.remove('d-none');
            }

            function setVisibleBullets($carousel: any, prev: number = 0, succ: number = 0, active: number = 0, init: boolean = false) {
                if ($carousel) {
                    const $splidePagination = $carousel.querySelector('.splide__pagination');
                    const $bullets = $splidePagination?.querySelectorAll('li');
                    if ($bullets?.length > 10 && window.innerWidth < 992) {
                        $bullets.forEach(($bullet: any, index: number) => {
                            if (init) {
                                if (prev < 10 && index >= 10) {
                                    $bullet.classList.add('d-none');
                                }
                            } else {
                                if (succ > prev) {
                                    if (active < 10 && index >= 10) {
                                        $bullet.classList.add('d-none');
                                    } else if (active >= 10) {
                                        if (active === index) {
                                            $bullet.classList.remove('d-none');
                                        } else {
                                            if (index > active) {
                                                $bullet.classList.add('d-none');
                                            } else {
                                                if (index <= active - 10) {
                                                    $bullet.classList.add('d-none');
                                                } else {
                                                    $bullet.classList.remove('d-none');
                                                }
                                            }
                                        }
                                    }
                                } else {
                                    if (active < 10 && index >= 10) {
                                        $bullet.classList.add('d-none');
                                    } else {
                                        if (active < 10 && index < 10) {
                                            $bullet.classList.remove('d-none');
                                        } else {
                                            if (active >= 10 && index > active) {
                                                $bullet.classList.add('d-none');
                                            } else if (active >= 10 && index > active - 10) {
                                                $bullet.classList.remove('d-none');
                                            }
                                        }
                                    }
                                }
                            }
                        });
                    }
                }
            }

            function init() {
                bindUI();

                const $paginationDots = $item.querySelectorAll('.splide__pagination__page');
                if ($paginationDots) {
                    if ($paginationDots.length > 1) {
                        $paginationDots.forEach(($dot: any) => {
                            $dot.setAttribute('tabindex', '0');
                        });
                    } else {
                        $paginationDots.forEach(($dot: any) => {
                            $dot.classList.add('d-none');
                        });
                    }
                }
            }

            init();
        });
    }
}
